import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import client from '../../../api/client';
import {
  ToastErrorStyles,
  ToastSuccessStyles,
} from '../../../constants/toastStyles';
import { openToastAndSetContent } from '../../../redux/actions/toast/toastActions';

// Define types for our data
interface Bank {
  bankName: string;
  bankCode: string;
}
type BankResData = {
  name: string;
  countryId: number;
  bankCode: string;
  isMicrofinance: boolean | null;
  isMortgage: boolean | null;
  ussdBankCode: string;
  supportBankTransfer: boolean | null;
  logo: string | null;
  id: number;
  dateCreated: string;
  dateUpdated: string | null;
  dateDeleted: string | null;
  createdBy: number;
  updatedBy: string | null;
  deletedBy: string | null;
};
type BankRes = {
  data: BankResData[];
  status: boolean;
  message: string;
};
type UpdateBank = {
  data: null;
  status: boolean;
  message: string;
};
type DefaultBankRes = {
  data: BankResData;
  status: boolean;
  message: string;
};
const SetCollectionAcct = () => {
  const dispatch = useDispatch();
  const [banks, setBanks] = useState<Bank[]>([]);
  const [defaultBank, setDefaultBank] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [updating, setUpdating] = useState<boolean>(false);

  // Fetch banks and default bank on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch banks
        const banksResponse: { data: BankRes } = await client.get(
          '/settings/supported-bank-transfer'
        );
        const data = banksResponse.data.data.map((bank: BankResData) => ({
          bankName: bank.name,
          bankCode: bank.bankCode,
        }));
        setBanks(data);

        // Fetch default bank
        const defaultBankResponse: { data: DefaultBankRes } = await client.get(
          '/settings/default-bank'
        );
        console.log(defaultBankResponse);

        setDefaultBank(defaultBankResponse.data.data?.bankCode || '');
      } catch (err: any) {
        dispatch(
          openToastAndSetContent({
            toastStyles: ToastErrorStyles,
            toastContent: err.message || 'Failed to fetch bank data',
          })
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch]);

  // Handle bank selection change
  const handleChange = (event: SelectChangeEvent<string>) => {
    setDefaultBank(event.target.value);
  };

  // Update default bank
  const updateDefaultBank = async () => {
    try {
      setUpdating(true);
      const res: { data: UpdateBank } = await client.post(
        '/settings/default-bank',
        {
          bankCode: defaultBank,
        }
      );
      dispatch(
        openToastAndSetContent({
          toastStyles: ToastSuccessStyles,
          toastContent: res.data.message || 'Default bank updated successfully',
        })
      );

      // Refresh default bank data
      const defaultBankResponse: { data: DefaultBankRes } = await client.get(
        '/settings/default-bank'
      );
      setDefaultBank(defaultBankResponse.data.data?.bankCode || '');
    } catch (err: any) {
      dispatch(
        openToastAndSetContent({
          toastStyles: ToastErrorStyles,
          toastContent: err.message || 'Failed to update default bank',
        })
      );
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Card sx={{ maxWidth: '550px', width: '100%', my: 5 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Set Default Collection Bank
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" my={3}>
            <CircularProgress size={30} />
          </Box>
        ) : (
          <>
            <FormControl fullWidth margin="normal">
              <InputLabel style={{ color: 'rgba(34, 41, 47, 0.4)' }}>
                Set Default Bank
              </InputLabel>
              <Select
                value={defaultBank}
                onChange={handleChange}
                label="Set Default Bank"
              >
                {banks.map((bank) => (
                  <MenuItem key={bank.bankCode} value={bank.bankCode}>
                    {bank.bankName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                color="success"
                onClick={updateDefaultBank}
                disabled={updating || !defaultBank}
              >
                {updating ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Save Changes'
                )}
              </Button>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default SetCollectionAcct;
